import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories, mainSiteUrl } from "@/mocks/categories";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id: string) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    }
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-background-200 bg-white transition-shadow ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <nav className="mx-auto flex max-w-[1280px] items-center justify-between px-4 py-3 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-layout-grid-line w-5 h-5 flex items-center justify-center" />
          </span>
          <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
            AllChinaBuy <span className="text-primary-600">Spreadsheet</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-0.5 md:flex">
          <button
            onClick={goHome}
            className="rounded-full px-3.5 py-2 text-sm font-semibold text-foreground-800 whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100"
          >
            {t("nav.home")}
          </button>

          {/* Categories dropdown */}
          <div className="relative">
            <button
              onClick={() => setCatOpen((v) => !v)}
              className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold text-foreground-800 whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100"
            >
              {t("nav.categories")}
              <i
                className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${
                  catOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {catOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setCatOpen(false)} />
                <div className="absolute left-0 z-50 mt-2 max-h-[70vh] w-72 overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-1.5 shadow-md">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.href}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setCatOpen(false)}
                      className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-100">
                        <img
                          src={cat.image}
                          alt=""
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      {t(`categories.${cat.key}`)}
                      <i className="ri-arrow-right-up-line ml-auto w-4 h-4 flex items-center justify-center text-foreground-400" />
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => goTo("guides")}
            className="rounded-full px-3.5 py-2 text-sm font-semibold text-foreground-800 whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100"
          >
            {t("nav.guides")}
          </button>

          <Link
            to="/article/allchinabuy-buying-guide"
            className="rounded-full px-3.5 py-2 text-sm font-semibold text-foreground-800 whitespace-nowrap transition-colors hover:bg-background-100"
          >
            {t("nav.buyingGuide")}
          </Link>

          <button
            onClick={() => goTo("faq")}
            className="rounded-full px-3.5 py-2 text-sm font-semibold text-foreground-800 whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100"
          >
            {t("nav.faq")}
          </button>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            {t("hero.ctaBuy")}
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground-900 cursor-pointer hover:bg-background-100 md:hidden"
            aria-label="Menu"
          >
            <i
              className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} w-5 h-5 flex items-center justify-center`}
            />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-6 pt-3 md:hidden">
          {/* Primary mobile actions */}
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-1.5 rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center" />
              {t("nav.home")}
            </Link>
            <Link
              to="/article/allchinabuy-buying-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-1.5 rounded-full border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800"
            >
              <i className="ri-book-open-line w-4 h-4 flex items-center justify-center" />
              {t("nav.buyingGuide")}
            </Link>
          </div>

          {/* Categories with thumbnails */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground-900">
              {t("nav.categories")}
            </span>
            <LanguageSwitcher />
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.href}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-1.5 transition-colors hover:bg-background-100"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-100">
                  <img
                    src={cat.image}
                    alt=""
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <span className="truncate text-xs font-medium text-foreground-800">
                  {t(`categories.${cat.key}`)}
                </span>
              </a>
            ))}
          </div>

          {/* Secondary links */}
          <div className="mt-3 flex flex-col gap-1 border-t border-background-200 pt-3">
            <button
              onClick={() => goTo("guides")}
              className="rounded-md px-3 py-2 text-left text-sm text-foreground-700 hover:bg-background-100"
            >
              {t("nav.guides")}
            </button>
            <button
              onClick={() => goTo("faq")}
              className="rounded-md px-3 py-2 text-left text-sm text-foreground-700 hover:bg-background-100"
            >
              {t("nav.faq")}
            </button>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-1 rounded-full bg-primary-500 px-4 py-2.5 text-center text-sm font-semibold text-background-50"
            >
              {t("hero.ctaBuy")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}